/**
 * Traditional Chinese (繁體中文) — shared/top-level component strings.
 * Product names (MeshGen, SimOps, Diagnosis) stay untranslated (brand).
 */
export default {
  transcriptLink: {
    view: '檢視 AI 對話記錄',
  },
  pipeline: {
    ariaLabel: '產品流程',
    diagnosisHint: '檢查並修復 CAD,直到可以劃分網格。',
    meshgenHint: '為乾淨的幾何劃分網格。',
    simopsHint: '模擬網格 — 單一案例或完整研究。',
    evaltoolHint: '評估結果並比較設計 — 位於已完成研究內的評估工作台。',
  },
  common: {
    beta: 'Beta',
    unknownError: '未知錯誤',
  },
  entitlement: {
    noSeat:
      '您在此工作區沒有 {{product}} 的席位。請工作區管理員為您指派席位，或聯絡支援團隊。',
    noSubscription:
      '此工作區沒有 {{product}} 的有效訂閱。請工作區管理員訂閱此產品，或聯絡支援團隊。',
    thisProduct: '此產品',
  },
  apiRefusal: {
    artifactTooLarge:
      '此執行的結果太大，無法在此讀取。請下載執行結果，並在 ParaView 中開啟。',
  },
  fileUpload: {
    tooLarge: '檔案太大（{{size}}）。上限為 1 GB。',
    invalidType: '檔案類型無效。僅接受 STEP、STP、OBJ 與 STL 檔案。',
    rejected: '檔案已遭拒。',
    uploading: '正在上傳 {{name}}…',
    uploadingPercent: '正在上傳 {{percent}}%',
    uploadingNoName: '正在上傳…',
    openCadFile: '開啟 CAD 檔案…',
    dragAndDrop: '將檔案拖曳至此',
    limit: '每個檔案上限 1GB • STEP、STP、OBJ、STL',
    browseFiles: '瀏覽檔案',
  },
  floatingProgress: {
    processing: '處理中',
    liveComputing: '即時運算',
  },
  meshTimer: {
    running: '執行中',
    complete: '已完成',
    error: '錯誤',
    ready: '就緒',
  },
  processStatus: {
    noActiveProject: '沒有進行中的專案',
    status: '狀態',
    fileUploaded: '檔案已上傳',
    generatingMesh: '正在產生網格',
    meshComplete: '網格完成',
    strategy: '策略',
  },
  promoCode: {
    havePromoCode: '有優惠碼嗎？',
    enterCode: '輸入優惠碼',
    redeem: '兌換',
  },
  qualityHistogram: {
    distribution: '品質分佈',
    noData: '沒有可用的品質資料',
    clickPrefix: '點擊',
    generateMesh: '產生網格',
    clickSuffix: '以建立網格並檢視品質指標。',
    excellent: '極佳',
    good: '良好',
    fair: '尚可',
    poor: '不佳',
    bad: '很差',
    badToGood: '很差 → 良好',
    estimated: '～估算值',
    realData: '真實資料',
    min: '最小',
    avg: '平均',
    max: '最大',
    elements: '{{count}} 個網格單元',
    scaleTitle: 'SICN 品質量表：',
    scaleBad: '0.0-0.2：很差',
    scalePoor: '0.2-0.4：不佳',
    scaleFair: '0.4-0.6：尚可',
    scaleGood: '0.6-0.8：良好',
    scaleGreat: '0.8-1.0：極佳',
  },
  qualityReport: {
    title: '品質報告',
    pass: '通過',
    flag: '警示',
    elements: '網格單元',
    poor: '不佳（<0.1）',
    sicnMin: 'SICN（最小）',
    gammaMin: 'Gamma（最小）',
    maxSkewness: '最大歪斜度',
    maxAspectRatio: '最大長寬比',
    cfdQuality: 'CFD 品質',
    ready: '就緒',
    issues: '有問題',
    nonOrthogonality: '非正交性',
    cfdSkewness: 'CFD 歪斜度',
    facePyramids: '面金字塔',
    ok: 'OK',
    facePyramidsFlag: '警示',
    cfdReadyMesh: 'CFD 就緒網格（非正交 ≤70°）',
    meetsStandards: '網格品質符合所有標準',
  },
  shareLink: {
    copyPublicLink: '複製公開連結',
  },
  visibilityBadge: {
    makePrivate: '點擊以設為私人',
    makePublic: '點擊以設為公開',
    public: '公開',
    private: '私人',
  },
  shareDialog: {
    shareButton: '分享',
    title: '分享',
    description: '管理誰可以檢視、留言或編輯此項目。',
    peopleWithAccess: '可存取的使用者',
    roleOwner: '擁有者',
    roleViewer: '檢視者',
    roleCommenter: '可留言',
    roleEditor: '可編輯',
    pendingInvite: '（已邀請）',
    removeAccess: '移除 {{email}} 的存取權',
    invitePlaceholder: '輸入電子郵件以新增使用者',
    invite: '邀請',
    invalidEmail: '請輸入有效的電子郵件地址。',
    toastInvited: '已邀請 {{email}}。',
    toastInvitedEmailFailed: '已新增 {{email}}，但邀請郵件寄送失敗。',
    generalAccess: '一般存取權',
    scopeRestricted: '受限',
    scopeOrganization: '您的組織',
    scopeAnyoneWithLink: '知道連結的任何人',
    scopeRestrictedHint: '只有具備存取權的使用者才能開啟。',
    scopeOrganizationHint: '您組織內的任何人都能開啟。',
    scopeAnyoneWithLinkHint: '知道連結的任何人都能開啟。',
    copyLink: '複製連結',
  },
  dataTable: {
    noResults: '沒有結果。',
  },
  geometryNotFound: {
    title: '找不到幾何',
    goToDashboard: '前往儀表板',
    loadFailed: '載入此幾何時發生錯誤。',
  },
  staleWarning: {
    messageProject:
      '此專案上次處理是在一週多以前，因此部分較新的功能可能無法與其搭配使用。',
    messageStudy:
      '此研究上次處理是在一週多以前，因此部分較新的功能可能無法與其搭配使用。',
    messageGeometry:
      '此幾何上次處理是在一週多以前，因此部分較新的功能可能無法與其搭配使用。',
    actionReupload: '重新上傳',
    actionCreateStudy: '建立新研究',
    actionRerunDiagnosis: '重新執行 Diagnosis',
    dismiss: '關閉警告',
  },
  toast: {
    downloadSimulationFailed: '無法下載模擬輸出',
    downloadInputCaseFailed: '無法下載輸入案例',
    visibilityUpdateFailed: '無法更新公開狀態',
    sharingUpdateFailed: '無法更新共用設定',
  },
  readOnlyBanner: {
    viewerAccess: '您擁有檢視者權限 — 擁有者為 {{owner}}',
    commenterAccess: '您擁有留言者權限 — 擁有者為 {{owner}}',
    viewerAccessNoOwner: '您擁有檢視者權限',
    commenterAccessNoOwner: '您擁有留言者權限',
    anonymousAccess: '您擁有唯讀權限',
    openInApp: '在應用程式中開啟',
    signInToOpenInApp: '登入',
    preferences: '偏好設定',
    signInPrompt: '您需要存取權',
    signInBody: '請使用具備檢視權限的帳號登入。',
    signInCta: '登入',
  },
} as const;
